import React, { useState, useEffect } from 'react';
import { Card, EffectStatus, CardConsequence } from '../../types';
import PlayableCard from './PlayableCard';
import ActiveEffectsDisplay from '../ActiveEffectsDisplay';
import { useSoundEffects } from '../../context/SoundEffectsContext';
import './ParadoxCardDetail.css';

interface ParadoxCardDetailProps {
    card: Card;
    activeEffects: EffectStatus[];
    onPlayCard: (cardName: string) => void;
    onBack: () => void;
    isLoading: boolean;
}

const formatCardName = (name: string): string => {
    return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const ParadoxCardDetail: React.FC<ParadoxCardDetailProps> = ({
    card,
    activeEffects,
    onPlayCard,
    onBack,
    isLoading
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isDissolving, setIsDissolving] = useState(false);
    const { playSound } = useSoundEffects();

    const isJunkCard = card.type === 'junk';

    useEffect(() => {
        requestAnimationFrame(() => {
            setIsVisible(true);
        });
    }, []);

    const handleBack = () => {
        playSound('ui_window_close');
        setIsVisible(false);
        setTimeout(onBack, 300);
    };

    const handlePlayCard = () => {
        if (isJunkCard) {
            setIsDissolving(true);
            playSound('press_enter');
            setTimeout(() => {
                onPlayCard(card.name);
            }, 800);
        } else {
            playSound('press_enter');
            onPlayCard(card.name);
        }
    };

    return (
        <div className={`paradox-detail-overlay ${isVisible ? 'visible' : ''}`} onClick={handleBack}>
            <div
                className={`paradox-detail-modal ${isVisible ? 'visible' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button className="paradox-detail-back" onClick={handleBack} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6,9 12,15 18,9" />
                    </svg>
                    <span>Back</span>
                </button>

                <div className="paradox-detail-content">
                    <div className="paradox-detail-top">
                        <PlayableCard
                            card={card}
                            className={`paradox-detail-preview ${isDissolving ? 'dissolving' : ''}`}
                        />
                        <div className="paradox-detail-info">
                            <h2 className="paradox-detail-title">{formatCardName(card.name)}</h2>
                            <span className={`paradox-detail-type ${isJunkCard ? 'junk' : 'paradox'}`}>
                                {isJunkCard ? 'Junk Card' : 'Paradox Card'}
                            </span>
                            {card.description && (
                                <p className="paradox-detail-description">{card.description}</p>
                            )}
                            {card.consequences && card.consequences.length > 0 && (
                                <div className="paradox-detail-consequences">
                                    {card.consequences.map((c: CardConsequence, i: number) => (
                                        <span
                                            key={i}
                                            className={`paradox-detail-consequence ${c.is_positive === false ? 'negative' : 'positive'}`}
                                        >
                                            {c.description}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Show existing active effects */}
                    {activeEffects.length > 0 && (
                        <div className="paradox-detail-modifiers">
                            <h3 className="paradox-detail-section-title">Current Modifiers</h3>
                            <ActiveEffectsDisplay effects={activeEffects} variant="full" />
                        </div>
                    )}

                    <div className="paradox-detail-bottom">
                        {isJunkCard && (
                            <p className="paradox-detail-warning">
                                Playing this card will remove it from the game permanently.
                            </p>
                        )}

                        <button
                            className={`paradox-detail-play-btn ${isJunkCard ? 'junk' : ''}`}
                            onClick={handlePlayCard}
                            disabled={isLoading || isDissolving}
                        >
                            {isLoading || isDissolving ? 'Removing...' : 'Play Card'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParadoxCardDetail;
